import {
  useGetBackListeElettoraliIdMembri,
  type MembroListaElettorale,
} from '@framework/api-client';
import type { IWidgetComponentProps } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import {
  formatDateFilter,
  getDateRange,
  getNetworkErrorMessage,
  getPagination,
  TableData,
} from '@framework/ui';
import { message } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useMemo, useState } from 'react';
import { generateCandidateLabel } from '../form/generateCandidateLabel';
import { type ICandidateWidget } from '../ICandidateWidget';
import { useCandidateWidgetColumnsTypes } from './useCandidateWidgetColumnsTypes';

export interface ICandidateMembersTableDataProps
  extends IWidgetComponentProps<ICandidateWidget> {
  setSelected: (member: MembroListaElettorale | null) => void;
  selected: MembroListaElettorale | null;
}

/**
 * Tabella per la selezione dei membri della lista elettorale
 */
export function CandidateMembersTableData(
  props: ICandidateMembersTableDataProps,
) {
  const { widget, setSelected, selected } = props;
  const { t } = useTranslation();

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
      }),
    [],
  );

  const [query, setQuery] = useState(defaultQuery);

  const { data: listMembers, isLoading } = useGetBackListeElettoraliIdMembri(
    widget.electoralListId,
    {
      ...query,
      // Di default passiamo un array di ordinamento
      ordinamenti: query.ordinamenti ?? ['-isCandidato', 'cognome'],
      isEleggibile: true,
    },
    {
      query: {
        enabled: widget.electoralListId != null,
        onError: error => {
          message.error(
            getNetworkErrorMessage(
              error,
              t(
                'widgets.candidate.members-load-error',
                'Errore durante il caricamento dei membri della lista elettorale',
              ),
            ),
          );
        },
      },
      request: { arrayFormat: 'repeat' },
    },
  );

  const meta = listMembers?.meta;

  const columns = useCandidateWidgetColumnsTypes(widget, query);

  const onPaginationChange = (
    pagination: any,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<MembroListaElettorale>
      | SorterResult<MembroListaElettorale>[],
  ) => {
    const sortArray = Array.isArray(sorter) ? sorter : [sorter];
    const activeSorter = sortArray.find(s => s.order);

    const ordinamenti =
      activeSorter?.field && activeSorter.order
        ? [
            `${activeSorter.order === 'descend' ? '-' : ''}${
              activeSorter.field
            }`,
          ]
        : undefined;

    const birthDates = getDateRange(filters?.dataNascita);

    setQuery(prev => ({
      ...prev,
      pagina: (pagination.current ?? 1) - 1,
      limite: pagination.pageSize ?? prev.limite,
      ordinamenti,
      nome: filters?.nome?.[0] as string | undefined,
      cognome: filters?.cognome?.[0] as string | undefined,
      profilo: filters?.profilo?.[0] as string | undefined,
      codiceFiscale: filters?.codiceFiscale?.[0] as string | undefined,
      dataNascitaDa: formatDateFilter(birthDates.from),
      dataNascitaA: formatDateFilter(birthDates.to),
    }));
  };

  return (
    <TableData
      removeShadow
      dataSource={listMembers?.dati ?? []}
      size="small"
      style={{ marginTop: '6px' }}
      loading={isLoading}
      pagination={{
        current: Number(query.pagina ?? 0) + 1,
        pageSize: query.limite != null ? Number(query.limite) : 10,
        total: meta?.totale ?? 0,
      }}
      onChange={onPaginationChange}
      columns={columns}
      darkTitleBar={false}
      rowSelection={{
        type: 'radio',
        onChange: (_, selectedRows) => {
          setSelected(selectedRows[0]);
        },
        getCheckboxProps: record => ({
          disabled: !record.isEleggibile,
          // A11y: Accessibilità selettore riga
          'aria-label': `Seleziona ${generateCandidateLabel(
            record,
            widget.labelFieldColumns ?? [],
          )}`,
          name: 'candidate-table-radio-group',
        }),
        selectedRowKeys: selected?.id != null ? [selected.id] : undefined,
      }}
    />
  );
}
